/**
 * @file ReceiptItemLotFields.tsx
 * @description Поля партии в форме позиции приёмки: цены, номер партии,
 * ячейка и даты (вынесено из `ReceiptItemForm` в phase19.23 этап A; скан
 * ячейки/уровня и срок «мягко» — phase19.22, W14-9, W20).
 *
 * Контракт — общий `value/onChange` (как `CarrierMainFields`): форма позиции
 * держит одно состояние партии, а секция только рисует поля.
 *
 * phase19.31 (SC51, W11): кладовщик цену продажи не видит — в режиме
 * кладовщика поле «Цена продажи» не рисуется (`showSalePrice={false}`), а
 * сборка запроса её не отправляет (`receipt-item-payload`). Высота полей —
 * из темы; моноширинный — только у номера партии (это код).
 */

import { BinScanField } from "./bins/BinScanField";
import { ExpiryFields } from "./ExpiryFields";
import { WmsFieldLabel } from "./WmsFieldLabel";
import type { StorageBinDto } from "../../lib/api-bins";
import type { ExpiryPolicy } from "../../lib/receipt-expiry";

/** Данные партии, которые кладовщик вводит руками. */
export interface ReceiptItemLotValue {
  unitCost: string;
  salePrice: string;
  batchNumber: string;
  /** id ячейки; пусто — «по умолчанию у фасовки». */
  binId: string;
  manufacturedAt: string;
  expiresAt: string;
  /** Срок вписан руками — автосчёт от даты изготовления его не трогает. */
  expiresManual: boolean;
}

interface Props {
  value: ReceiptItemLotValue;
  onChange: (next: ReceiptItemLotValue) => void;
  /** Склад документа — резолвер скана ячейки. */
  warehouseId: string;
  /** Ячейки склада (подпись выбранной); пустой список — адресного хранения нет. */
  bins: StorageBinDto[];
  /** Требует ли вид товара срок и сколько дней подставлять (W20). */
  expiryPolicy: ExpiryPolicy;
  /** Показывать «Цену продажи»; в режиме кладовщика — нет (SC51). */
  showSalePrice?: boolean;
  /**
   * Поля неактивны — фасовка ещё не выбрана (Б15, phase19.31): на широком
   * экране панель видна целиком, и даты, введённые в пустую форму, «уходили
   * в никуда» — человек ждал, что они применятся к уже добавленной строке.
   */
  disabled?: boolean;
  /**
   * Ошибка цены под полями цен (правка строки черновика, Б16): своя
   * проверка или отказ сервера 422 `receipt_item_price_invalid`.
   */
  pricesError?: string | null;
}

/** Высота поля — из темы (48/44, у кладовщика до 1024 — 56). */
const INPUT_CLS =
  "min-h-[var(--control-h,3rem)] w-full rounded-lg border border-on-surface/15 bg-surface-lowest px-3 text-[length:var(--fs-field,1rem)] disabled:cursor-not-allowed disabled:bg-surface-low disabled:text-on-surface-variant";

export function ReceiptItemLotFields({
  value,
  onChange,
  warehouseId,
  bins,
  expiryPolicy,
  showSalePrice = true,
  disabled = false,
  pricesError,
}: Props) {
  /** Точечное обновление одного поля — форма хранит объект целиком. */
  function patch(part: Partial<ReceiptItemLotValue>) {
    onChange({ ...value, ...part });
  }
  const priceInvalid = Boolean(pricesError);

  return (
    <>
      <div className={showSalePrice ? "grid grid-cols-2 gap-2" : "grid grid-cols-1 gap-2"}>
        <WmsFieldLabel label="Закупочная ₽">
          <input
            type="text"
            value={value.unitCost}
            onChange={(e) => patch({ unitCost: e.target.value })}
            inputMode="decimal"
            placeholder="0"
            disabled={disabled}
            aria-invalid={priceInvalid || undefined}
            className={`${INPUT_CLS} tabular-nums`}
          />
        </WmsFieldLabel>
        {showSalePrice ? (
          <WmsFieldLabel label="Цена продажи ₽">
            <input
              type="text"
              value={value.salePrice}
              onChange={(e) => patch({ salePrice: e.target.value })}
              inputMode="decimal"
              placeholder="0"
              disabled={disabled}
              aria-invalid={priceInvalid || undefined}
              className={`${INPUT_CLS} tabular-nums`}
            />
          </WmsFieldLabel>
        ) : null}
      </div>
      {pricesError ? <p role="alert" className="type-caption text-destructive">{pricesError}</p> : null}

      <WmsFieldLabel label="№ партии">
        <input
          type="text"
          value={value.batchNumber}
          onChange={(e) => patch({ batchNumber: e.target.value })}
          placeholder="пусто — номер присвоит система"
          disabled={disabled}
          className={`${INPUT_CLS} font-mono`}
        />
      </WmsFieldLabel>

      {/* Не `<label>`: внутри поля ячейки есть кнопки-плитки и сброс. */}
      {bins.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="label-editorial">Ячейка</span>
          <BinScanField
            warehouseId={warehouseId}
            bins={bins}
            value={value.binId}
            onChange={(binId) => patch({ binId })}
            emptyOptionLabel="по умолчанию у фасовки"
            disabled={disabled}
          />
        </div>
      ) : null}

      <ExpiryFields
        value={value}
        onChange={(next) =>
          patch({
            manufacturedAt: next.manufacturedAt,
            expiresAt: next.expiresAt,
            expiresManual: next.expiresManual,
          })
        }
        policy={expiryPolicy}
        disabled={disabled}
      />
    </>
  );
}
